// Article « ASSR 1 — Comment bien la préparer en 5ᵉ »
// Guide permanent à destination des élèves de 5ᵉ, parents et professeurs EMC.
// Pont pédagogique : ASSR 1 → Permis AM (BSR) → scooter 50 cm³ dès 14 ans.

import {
  GraduationCap, School, Users, CheckCircle2, AlertTriangle,
  BookOpen, Clock, ShieldCheck, Bike, Lightbulb, HeartPulse,
  Eye, Target, Award, Compass,
} from 'lucide-react';

export const assr1Article = {
  slug: 'assr1-preparer-5eme',
  weekVolume: null,
  weekYear: 2026,
  monthLabel: 'Guide permanent',
  category: 'ASSR & scolarité',
  isGuide: true,
  shortTitle: "ASSR 1 — Préparer en 5ᵉ",
  title: "ASSR 1 : comment bien la préparer en classe de 5ᵉ",
  kicker: 'Guide élève, parent, professeur',
  subtitle:
    "L'ASSR 1 est la première attestation officielle de sécurité routière, passée en fin de 5ᵉ. Elle ouvre la porte au BSR / permis AM et donc au scooter 50 cm³ dès 14 ans. Voici comment t'y préparer efficacement.",
  heroImage: '/images/articles/route-france-signalisation.jpg?v=20260514',
  heroImageAlt:
    "Route française péri-urbaine avec panneaux de signalisation et passages piétons — environnement typique de l'apprentissage de la sécurité routière au collège (ASSR1)",
  topRightKicker: { line1: 'Guide scolaire', line2: '5ᵉ · ≈ 13 ans' },
  readTimeMin: 6,
  seoDescription:
    "ASSR 1 en 5ᵉ : comment se préparer, contenu de l'examen, lien avec le BSR / permis AM, entraînement officiel gratuit. Le guide ECSR pour élèves, parents et professeurs.",
  seoKeywords:
    "ASSR1, ASSR 1, Attestation Sécurité Routière 5ème, BSR, permis AM, scooter 14 ans, entraînement ASSR officiel, préparer ASSR1, éducation sécurité routière",
  about:
    "Préparation à l'Attestation Scolaire de Sécurité Routière niveau 1",
  source: {
    label: 'Ministère de l\'Éducation nationale — e-ASSR officiel',
    url: 'https://e-assr.education-securite-routiere.fr/preparer/assr/1/ASSR1',
    refs: "Arrêté du 25 mars 2007 relatif aux ASSR et à l'ASR.",
  },

  chapo: {
    kicker: 'Pourquoi cet article',
    body:
      "L'<strong>ASSR 1</strong> est la première étape officielle de ton éducation à la sécurité routière. Elle se passe en <strong>fin de 5ᵉ</strong>, au collège, pendant un créneau banalisé. Sans elle, pas de <strong>BSR (permis AM)</strong>, donc pas de scooter 50 cm³ à 14 ans. Cet article te dit exactement comment t'y préparer, ce qui tombe, et comment bien t'entraîner gratuitement sur le site officiel du Ministère.",
    sideStat: {
      kicker: 'En chiffres',
      big: '10/20',
      body:
        "la note minimale pour valider l'ASSR 1 (soit <strong>20 bonnes réponses sur 20 questions</strong> à coefficient 1). L'examen dure <strong>30 minutes</strong> environ.",
      accent: '#0ea5e9',
    },
  },

  pullQuote: {
    text:
      "L'ASSR 1 n'est pas un obstacle : c'est ta première clé vers la mobilité autonome. À 14 ans, avec le BSR, tu deviens responsable de ta propre sécurité sur la route.",
    source: 'Moniteurs ECSR — accompagnement jeunes',
  },

  sections: [
    {
      part: 'Partie 1 — À quoi ça sert ?',
      title: "Pourquoi l'ASSR 1 est importante",
      intro:
        "Au-delà du côté administratif, l'ASSR 1 t'ancre <strong>les bons réflexes</strong> dès le plus jeune âge. Elle conditionne aussi deux choses concrètes dans ta vie d'adolescent.",
      cards: [
        {
          icon: Bike,
          accent: '#0ea5e9',
          kicker: 'Usage immédiat',
          title: "Le BSR / permis AM",
          body:
            "À 14 ans, pour conduire un <strong>scooter 50 cm³</strong> ou un <strong>quadricycle léger</strong> (voiturette), il faut le permis AM. Et pour le passer, il faut <strong>ASSR 1 + une formation pratique de 8 h</strong> en moto-école.",
        },
        {
          icon: Award,
          accent: '#16a34a',
          kicker: 'Valable à vie',
          title: "Un acquis permanent",
          body:
            "Une fois obtenue, l'ASSR 1 est <strong>valable toute ta vie</strong>. Tu ne la repasseras jamais. Prends-la donc au sérieux — le jour J compte pour toujours.",
        },
        {
          icon: ShieldCheck,
          accent: '#cf2031',
          kicker: 'Citoyenneté',
          title: "Une vraie base sécurité",
          body:
            "Au-delà du permis, elle te donne les bons réflexes pour <strong>traverser en piéton</strong>, <strong>faire du vélo</strong>, <strong>monter en voiture</strong> ou <strong>agir en cas d'accident</strong>. Des compétences utiles toute la vie.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 2 — Le contenu',
      title: "Ce qui tombe à l'examen",
      intro:
        "L'ASSR 1 se passe en 30 minutes devant un ordinateur ou sur papier. Elle contient <strong>20 questions à choix multiples</strong>, chacune basée sur une courte vidéo ou une image. Voici les 4 grandes thématiques.",
      cards: [
        {
          icon: Eye,
          accent: '#1d4ed8',
          kicker: 'Thème 1',
          title: 'Le piéton',
          body:
            "Traverser en sécurité, regarder à <strong>gauche-droite-gauche</strong>, utiliser les passages protégés, respecter le feu piéton. Règles à pied sur la chaussée (route sans trottoir).",
        },
        {
          icon: Users,
          accent: '#16a34a',
          kicker: 'Thème 2',
          title: 'Le passager',
          body:
            "Ceinture obligatoire (même derrière), comportement en voiture (pas de distraction du conducteur), siège adapté, descendre côté trottoir, casque à vélo/scooter même comme passager.",
        },
        {
          icon: Bike,
          accent: '#f59e0b',
          kicker: 'Thème 3',
          title: 'Le cycliste',
          body:
            "Équipement obligatoire (<strong>casque jusqu'à 12 ans</strong>, gilet jaune de nuit, freins, éclairage), règles de circulation (piste cyclable, sas vélo), priorités et signalisation.",
        },
        {
          icon: HeartPulse,
          accent: '#cf2031',
          kicker: 'Thème 4',
          title: 'Les premiers secours',
          body:
            "Les gestes <strong>PAS</strong> : <strong>P</strong>rotéger la zone, <strong>A</strong>lerter (112, 15, 18), <strong>S</strong>ecourir. Ne jamais retirer le casque d'un blessé.",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 3 — La méthode',
      title: "Comment bien se préparer en 3 étapes",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: '✓ Méthode gagnante',
          title: "3 étapes simples",
          intro: "Ce qui marche vraiment pour décrocher l'ASSR 1 :",
          points: [
            { ok: true, text: "<strong>1. S'entraîner sur le site officiel</strong> (gratuit, illimité, avec les VRAIES questions type)" },
            { ok: true, text: "<strong>2. Regarder les vidéos en classe</strong> — ne pas zapper les cours d'EMC ou d'ASSR" },
            { ok: true, text: "<strong>3. Refaire les quiz 2 ou 3 fois</strong> avant la date officielle — la répétition ancre les réflexes" },
            { ok: true, text: "Lire ensemble avec un parent : comprendre les réponses, pas juste les mémoriser" },
            { ok: true, text: "Observer dans la vraie vie : trajets en voiture, passages piétons, panneaux" },
          ],
        },
        {
          accent: '#cf2031',
          kicker: '✗ À éviter',
          title: "Les pièges classiques",
          intro: "Les erreurs qui font rater le jour J :",
          points: [
            { ok: false, text: "Réviser la veille au soir uniquement (échec presque garanti)" },
            { ok: false, text: "Chercher des « corrigés » sur Internet — les questions changent régulièrement" },
            { ok: false, text: "Croire que « c'est facile, je saurai » sans jamais s'entraîner" },
            { ok: false, text: "Ne pas lire la question entièrement (piège classique : plusieurs bonnes réponses à cocher)" },
            { ok: false, text: "Paniquer sur une question difficile : passe et reviens-y après" },
          ],
          footer: "Rappel : 10/20 suffisent. Pas besoin d'être parfait — juste sérieux.",
        },
      ],
    },

    {
      part: 'Partie 4 — Et le jour J ?',
      title: "Déroulement de l'épreuve",
      intro:
        "L'ASSR 1 se passe <strong>dans ton collège</strong>, en groupe, à une date fixée par ton établissement (souvent au printemps). Voici ce qui t'attend.",
      cards: [
        {
          icon: Clock,
          accent: '#0ea5e9',
          kicker: 'Durée',
          title: '≈ 30 minutes',
          body:
            "20 questions en 30 minutes. Tu as largement le temps : <strong>1 min 30 par question</strong>. Pas de stress sur la vitesse.",
        },
        {
          icon: BookOpen,
          accent: '#16a34a',
          kicker: 'Format',
          title: 'Vidéos + questions',
          body:
            "Chaque question est <strong>précédée d'une courte vidéo</strong> (15 sec) ou d'une image. Tu dois observer puis cocher la/les bonne(s) réponse(s).",
        },
        {
          icon: Target,
          accent: '#cf2031',
          kicker: 'Validation',
          title: '10/20 minimum',
          body:
            "Tu as besoin de <strong>10 bonnes réponses sur 20</strong> pour être reçu. Si tu échoues, tu peux repasser l'examen en <strong>session de rattrapage</strong> au collège.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 5 — Parents & professeurs',
      title: "Accompagner un élève en 5ᵉ",
      callouts: [
        {
          icon: Lightbulb,
          accent: '#1d4ed8',
          body:
            "<strong>Pour les parents</strong> : un entraînement de <strong>30 minutes par semaine</strong> pendant un mois suffit. Faites-le ensemble sur le site officiel, discutez des réponses, reliez-les à vos propres trajets.",
        },
        {
          icon: School,
          accent: '#16a34a',
          body:
            "<strong>Pour les professeurs d'EMC</strong> : l'ASSR 1 est un <strong>excellent support d'évaluation citoyenne</strong>. Articulation naturelle avec les programmes sur la responsabilité, la loi et le vivre-ensemble.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body:
            "<strong>Élève absent le jour J ?</strong> Une <strong>session de rattrapage</strong> est organisée par le collège. En cas d'impossibilité répétée, l'élève pourra passer l'<strong>ASR dans un GRETA</strong> plus tard (même équivalence).",
        },
      ],
    },
  ],

  quiz: [
    {
      id: 'q1',
      question: "Quelle note minimale faut-il pour valider l'ASSR 1 ?",
      options: ['8/20', '10/20', '12/20', '15/20'],
      correctIndex: 1,
      explain:
        "10 bonnes réponses sur 20 suffisent pour être reçu à l'ASSR 1. L'objectif n'est pas la perfection — c'est de vérifier l'acquisition des réflexes de base.",
    },
    {
      id: 'q2',
      question: "À quel âge peut-on conduire un scooter 50 cm³ avec le permis AM ?",
      options: ['12 ans', '14 ans', '16 ans', '18 ans'],
      correctIndex: 1,
      explain:
        "Dès 14 ans avec le permis AM (ex-BSR) : il faut avoir obtenu l'ASSR 1 (ou AER) + suivi une formation pratique de 8 h en moto-école agréée.",
    },
    {
      id: 'q3',
      question: "L'ASSR 1 est valable combien de temps ?",
      options: ['5 ans', '10 ans', "Jusqu'à 18 ans", 'À vie'],
      correctIndex: 3,
      explain:
        "L'ASSR 1 est valable toute la vie. Une fois obtenue au collège, l'attestation reste valide indéfiniment — conserve précieusement l'original.",
    },
    {
      id: 'q4',
      question: "Le jour de l'ASSR 1, si tu bloques sur une question, que faut-il faire ?",
      options: [
        "Rester dessus jusqu'à trouver la réponse",
        "Répondre au hasard rapidement",
        "La passer et y revenir à la fin",
        "Demander à ton voisin",
      ],
      correctIndex: 2,
      explain:
        "Passe et reviens-y à la fin. Tu as 30 minutes pour 20 questions = largement le temps. Bloquer 5 minutes sur une seule question, c'est perdre du temps et se déstabiliser.",
    },
    {
      id: 'q5',
      question: "Quels sont les 3 gestes PAS (premiers secours), dans le bon ordre ?",
      options: [
        'Alerter, Protéger, Secourir',
        'Protéger, Alerter, Secourir',
        'Secourir, Alerter, Protéger',
        'Protéger, Secourir, Alerter',
      ],
      correctIndex: 1,
      explain:
        "P.A.S. = Protéger (sécuriser la zone) → Alerter (112, 15, 18) → Secourir. Jamais retirer le casque d'un blessé à moto.",
    },
  ],

  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: "Prêt à t'entraîner ?",
    body:
      "Le site officiel du Ministère de l'Éducation nationale propose un entraînement 100% gratuit avec les vraies questions type. Parfait pour s'exercer en conditions réelles.",
    primary: {
      label: "Voir toutes les ASSR (hub)",
      to: '/assr',
    },
    secondary: {
      label: "Voir les parcours permis (AM, A1, A2)",
      to: '/parcours',
    },
  },

  // Override mapping ETM (Option C) — ASSR1 touche à la base comportementale
  // (piéton, passager, cycliste, premiers secours). Pas directement mappable
  // à un thème ETM moto. On pointe vers L (Le conducteur) comme plus proche,
  // même si c'est l'utilité pédagogique large qui prime ici.
  etm: {
    primary: 'L',
    secondary: ['S', 'U'],
    label: 'Conducteur, usagers & premiers secours',
    cta: 'Réviser le thème « Le conducteur »',
  },
};
